import { useContext, useState } from 'react';
import {
  Box,
  Zoom,
  useTheme,
  Card,
  Tooltip,
  IconButton,
  Typography,
  useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { AuthContext, AuthContextType } from 'src/contexts/AuthContext';
import { ChatbotAdmin } from './ChatbotAdmin';
import { ChatbotChat } from './ChatbotChat';
import { useChatbotContext } from 'src/contexts/ChatbotContext';
import { ChatbotHistory } from './ChatbotHistory';

export const ChatbotPopup = ({
  setIsChatOpen,
  isChatOpen
}: {
  setIsChatOpen: (isOpen: boolean) => void;
  isChatOpen: boolean;
}) => {
  const theme = useTheme();
  const [isEnlarged, setIsEnlarged] = useState<boolean>(false);
  const isLargeViewport = useMediaQuery(theme.breakpoints.up('md'));
  const showEnlarged = isEnlarged || !isLargeViewport;
  const [chatbotMode, setChatbotMode] = useState<'chat' | 'load' | 'history' | 'admin'>('chat');
  const { memberid } = useContext(AuthContext) as AuthContextType;
  const isAdmin = ['626287'].includes(memberid ?? 'null'); // TODO: change this to be Role based. 'staff'?

  const { startNewConversation } = useChatbotContext();

  const transitionDuration = {
    enter: theme.transitions.duration.enteringScreen,
    exit: theme.transitions.duration.leavingScreen
  };

  const handleCloseClick = () => {
    setChatbotMode('chat');
    setIsChatOpen(false);
  };
  const handleResizeClick = () => {
    setIsEnlarged(!isEnlarged);
  };
  const handleNewConversationClick = () => {
    setChatbotMode('chat');
    startNewConversation();
  };
  const handleHistoryClick = () => {
    setChatbotMode('history');
  };
  const handleAdminClick = () => {
    if (chatbotMode === 'admin') {
      setChatbotMode('chat');
    } else {
      setChatbotMode('admin');
    }
  };

  return (
    <Zoom
      in={isChatOpen}
      timeout={transitionDuration}
      style={{
        transitionProperty: 'height',
        transformOrigin: 'bottom right',
        transitionDelay: `${isChatOpen ? transitionDuration.exit : 0}ms`
      }}
      unmountOnExit
    >
      <Box
        sx={{
          position: 'fixed',
          bottom: '5px',
          right: '5px',
          zIndex: 1100,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          [theme.breakpoints.up('md')]: {
            bottom: '30px',
            right: '5px'
          }
        }}
      >
        <Card
          elevation={5}
          sx={{
            height: 'calc(100vh - 5px - 5px)',
            width: 'calc(100vw - 5px - 20px)',
            transition:
              'width 200ms, height 200ms, max-height 200ms, transform 300ms cubic-bezier(0, 1.2, 1, 1), opacity 83ms ease-out',
            maxWidth: 'calc(100vw - 5px - 5px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            borderRadius: '16px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'stretch',
            [theme.breakpoints.up('md')]: {
              height: showEnlarged ? 'calc(100vh - 60px)' : '600px',
              width: showEnlarged ? 'min(calc(100vw - 5px - 20px), 688px)' : '400px'
            },
            overflowY: 'hidden'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              borderBottom: '1px solid #eee',
              gap: '8px',
              backgroundColor: '#11364d',
              color: '#fff'
            }}
          >
            <Box sx={{ padding: '16px' }}>
              <Typography variant="body1" color="inherit">
                RACGP CPD Home help
              </Typography>
              <Typography variant="body2" color="#fff">
                How can we help?
              </Typography>
            </Box>
            <Box
              sx={{
                marginLeft: 'auto',
                minWidth: 'initial',
                color: 'inherit',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end'
              }}
            >
              <Box>
                <Tooltip title="Start new conversation">
                  <IconButton
                    color="inherit"
                    aria-label="Start new conversation"
                    onClick={handleNewConversationClick}
                  >
                    <RestartAltIcon sx={{ color: 'inherit' }} />
                  </IconButton>
                </Tooltip>
                {isLargeViewport && (
                  <Tooltip title="Enlarge or shrink">
                    <IconButton color="inherit" aria-label="close" onClick={handleResizeClick}>
                      <OpenInFullIcon sx={{ color: 'inherit', transform: 'scaleX(-1)' }} />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Close">
                  <IconButton color="inherit" aria-label="close" onClick={handleCloseClick}>
                    <CloseIcon sx={{ color: 'inherit' }} />
                  </IconButton>
                </Tooltip>
              </Box>
              {isAdmin && (
                <Box>
                  <Tooltip title="Download feedback">
                    <IconButton
                      color="inherit"
                      aria-label="Download feedback"
                      onClick={handleAdminClick}
                    >
                      <SupervisorAccountIcon sx={{ color: 'inherit' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Previous conversations">
                    <IconButton
                      color="inherit"
                      aria-label="Previous conversations"
                      onClick={handleHistoryClick}
                    >
                      <AccessTimeIcon sx={{ color: 'inherit' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              padding: '16px',
              flex: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'stretch',
              overflowY: 'auto',
              marginBottom: '100px'
            }}
          >
            {chatbotMode === 'chat' && <ChatbotChat showEnlarged={showEnlarged} />}
            {chatbotMode === 'admin' && <ChatbotAdmin />}
            {chatbotMode === 'history' && (
              <ChatbotHistory setToChatModeChat={() => setChatbotMode('chat')} />
            )}
            {chatbotMode === 'load' && <>Load!</>}
          </Box>
        </Card>
      </Box>
    </Zoom>
  );
};

export default ChatbotPopup;
